import { useState, type ComponentProps, type ReactNode } from "react";
import { ActionModal, Button } from "@cntrl/ui";
import { useReopenSettlement } from "../../app/hooks/useMoney.js";

// The "Reopen" confirm, shared by the settlement panel's bottom row, the show preview's footer, AND the
// Outstanding Payments settle box (ART-241/248). ONE implementation so the surfaces cannot drift. It is a plain
// "are you sure" confirm — NO reason (the server records one when given but never requires it) — with a dynamic
// noun, because a settle box reopens a SHOW, a RUN, or a TOUR. Reopen reverts the worksheet AND the lifecycle dot
// (settled → confirmed) and is money:full, so a caller renders it only for a settled scope a money seat holds.

/** The bare confirm. `confirmTone="danger"` because reopening unlocks a locked deal for renegotiation. `noun` is
 *  the thing being reopened ("show" / "run" / "tour") — it names both the title and the question. */
export function ReopenModal({ noun = "show", onClose, onConfirm, loading }: { noun?: string; onClose: () => void; onConfirm: () => void; loading: boolean }) {
  return (
    <ActionModal
      title={`Reopen ${noun}`}
      size="sm"
      confirmLabel="Reopen"
      confirmTone="danger"
      onConfirm={onConfirm}
      onCancel={onClose}
      loading={loading}
    >
      <p>Are you sure you want to reopen this {noun}?</p>
    </ActionModal>
  );
}

type BtnStyle = Pick<ComponentProps<typeof Button>, "variant" | "size" | "tone">;

/** The trigger + its confirm + the mutation wiring, in one drop-in. The caller decides WHEN to render it (settled +
 *  money:full) and how the button LOOKS; the reopen flow itself is owned here. Show-scoped (useReopenSettlement). */
export function ReopenShowButton({ showId, children, ...btn }: { showId: string; children?: ReactNode } & BtnStyle) {
  const reopen = useReopenSettlement();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button {...btn} onClick={() => setOpen(true)}>
        {children ?? "Reopen show"}
      </Button>
      {open && (
        <ReopenModal
          loading={reopen.isPending}
          onClose={() => setOpen(false)}
          onConfirm={() => reopen.mutate({ showId, body: { reason: null } }, { onSuccess: () => setOpen(false) })}
        />
      )}
    </>
  );
}
